import type {
  AdminFunnelState,
  AdminSignupDto,
  AdminSignupResponse,
  Application,
  ApplicationDetail,
  ApplicationFilter,
  BeneficialOwner,
  CreateBeneficialOwnerDto,
  CreateInvitationDto,
  AdminInvitation,
  InvitationFilter,
  RejectApplicationDto,
  UpdateBeneficialOwnerDto,
  ValidateTokenResponse,
} from '@/types/adminOnboarding';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest, BASE_URL } from './base.service';

/**
 * Admin Onboarding Service
 * Handles all admin onboarding API endpoints from /api/v1/admin-onboarding/*
 */

export const adminOnboardingService = {
  // ─── Invitations (super admin) ─────────────────────────────────────────────

  async createInvitation(data: CreateInvitationDto): Promise<AdminInvitation> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<AdminInvitation>(
      '/api/v1/admin-onboarding/invitations',
      { method: 'POST', body: JSON.stringify(data) },
      token
    );
  },

  async getInvitations(filter?: InvitationFilter): Promise<AdminInvitation[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const params = new URLSearchParams();
    if (filter?.status) params.append('status', filter.status);

    const qs = params.toString();
    return authenticatedRequest<AdminInvitation[]>(
      `/api/v1/admin-onboarding/invitations${qs ? `?${qs}` : ''}`,
      {},
      token
    );
  },

  async cancelInvitation(id: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(
      `/api/v1/admin-onboarding/invitations/${id}`,
      { method: 'DELETE' },
      token
    );
  },

  // ─── Public ────────────────────────────────────────────────────────────────

  async validateToken(token: string): Promise<ValidateTokenResponse> {
    const response = await fetch(
      `${BASE_URL}/api/v1/admin-onboarding/invitations/validate/${token}`
    );
    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as { message?: string };
      throw new ApiError(err.message ?? 'Token inválido', response.status);
    }
    return response.json() as Promise<ValidateTokenResponse>;
  },

  // ─── Signup & Funnel (invited admin) ──────────────────────────────────────

  async signup(data: AdminSignupDto): Promise<AdminSignupResponse> {
    const response = await fetch(`${BASE_URL}/api/v1/admin-onboarding/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as { message?: string };
      throw new ApiError(err.message ?? 'Error al registrarse', response.status);
    }
    return response.json() as Promise<AdminSignupResponse>;
  },

  async getFunnelState(): Promise<AdminFunnelState> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<AdminFunnelState>(
      '/api/v1/admin-onboarding/funnel',
      {},
      token
    );
  },

  async submitStep(data: Record<string, unknown>): Promise<AdminFunnelState> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<AdminFunnelState>(
      '/api/v1/admin-onboarding/funnel/step',
      { method: 'POST', body: JSON.stringify(data) },
      token
    );
  },

  async goToStep(stepOrder: number): Promise<AdminFunnelState> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<AdminFunnelState>(
      `/api/v1/admin-onboarding/funnel/step/${stepOrder}`,
      { method: 'PUT' },
      token
    );
  },

  async uploadDocuments(stepOrder: number, formData: FormData): Promise<AdminFunnelState> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    formData.append('stepOrder', stepOrder.toString());

    const response = await fetch(`${BASE_URL}/api/v1/admin-onboarding/funnel/step/documents`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('finara_token');
      localStorage.removeItem('finara_token_expires');
      localStorage.removeItem('finara_user_id');
      throw new ApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
    }

    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as { message?: string };
      throw new ApiError(err.message ?? 'Error al subir documentos', response.status);
    }

    return response.json() as Promise<AdminFunnelState>;
  },

  async deleteDocument(documentId: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(
      `/api/v1/admin-onboarding/funnel/documents/${documentId}`,
      { method: 'DELETE' },
      token
    );
  },

  // ─── Beneficial Owners ─────────────────────────────────────────────────────

  async getBeneficialOwners(): Promise<BeneficialOwner[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<BeneficialOwner[]>(
      '/api/v1/admin-onboarding/funnel/beneficial-owners',
      {},
      token
    );
  },

  async createBeneficialOwner(data: CreateBeneficialOwnerDto): Promise<BeneficialOwner> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<BeneficialOwner>(
      '/api/v1/admin-onboarding/funnel/beneficial-owners',
      { method: 'POST', body: JSON.stringify(data) },
      token
    );
  },

  async updateBeneficialOwner(id: string, data: UpdateBeneficialOwnerDto): Promise<BeneficialOwner> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<BeneficialOwner>(
      `/api/v1/admin-onboarding/funnel/beneficial-owners/${id}`,
      { method: 'PUT', body: JSON.stringify(data) },
      token
    );
  },

  async deleteBeneficialOwner(id: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(
      `/api/v1/admin-onboarding/funnel/beneficial-owners/${id}`,
      { method: 'DELETE' },
      token
    );
  },

  async uploadBeneficialOwnerDocs(uboId: string, formData: FormData): Promise<BeneficialOwner> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const response = await fetch(
      `${BASE_URL}/api/v1/admin-onboarding/funnel/beneficial-owners/${uboId}/documents`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      }
    );

    if (response.status === 401) {
      localStorage.removeItem('finara_token');
      localStorage.removeItem('finara_token_expires');
      localStorage.removeItem('finara_user_id');
      throw new ApiError('Sesión expirada. Inicia sesión nuevamente.', 401);
    }

    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as { message?: string };
      throw new ApiError(err.message ?? 'Error al subir documentos', response.status);
    }

    return response.json() as Promise<BeneficialOwner>;
  },

  // ─── Review (super admin) ─────────────────────────────────────────────────

  async getApplications(filter?: ApplicationFilter): Promise<Application[]> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    const params = new URLSearchParams();
    if (filter?.status) params.append('status', filter.status);
    if (filter?.search) params.append('search', filter.search);

    const qs = params.toString();
    return authenticatedRequest<Application[]>(
      `/api/v1/admin-onboarding/review/applications${qs ? `?${qs}` : ''}`,
      {},
      token
    );
  },

  async getApplicationDetail(userId: string): Promise<ApplicationDetail> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<ApplicationDetail>(
      `/api/v1/admin-onboarding/review/applications/${userId}`,
      {},
      token
    );
  },

  async approveApplication(userId: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(
      `/api/v1/admin-onboarding/review/applications/${userId}/approve`,
      { method: 'POST' },
      token
    );
  },

  async rejectApplication(userId: string, data: RejectApplicationDto): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<void>(
      `/api/v1/admin-onboarding/review/applications/${userId}/reject`,
      { method: 'POST', body: JSON.stringify(data) },
      token
    );
  },
};
